import React, {useMemo} from "react";
import {Button, Card, CardBody, Row} from "reactstrap";
import NoSearchResult from "../../../../components/Common/NoSearchResult";
import CardOrder from "./CardOrder";
import Loader from "../../../../components/UI/loader";
import {ModalState, OrderActionKey} from "../constants/modals";
import {useInfiniteScroll} from "../../../../hooks/useInfiniteScroll";

interface TabPaneContentProps {
    trafficOrders?: any[];
    activeTabId: string;
    isLoading: boolean;
    isError: boolean;
    isSuccess: boolean;
    isFetchingNext: boolean;
    hasNextPage?: boolean;
    onLoadMore?: () => void;
    modalActions?: OrderActionKey[];
    toggleModal: (key: string, id?: string | null, date?: string | null, tabId?: string) => void;
}

const ACTION_TO_MODAL: Record<OrderActionKey, keyof ModalState> = {
    toggleAssignOrder: "isAssignOrder",
    toggleAssignIntegra: "isAssignIntegra",
    toggleInfoOrder: "isInfoOrder",
    toggleUpdate: "isUpdate",
    toggleChangeStatus: "isChangeStatus",
};

type ModalActionsMapType = Record<OrderActionKey, TabPaneContentProps["toggleModal"] | undefined>;

const TabPaneContent: React.FC<TabPaneContentProps> = ({
                                                           activeTabId,
                                                           isLoading,
                                                           isFetchingNext,
                                                           isError,
                                                           isSuccess,
                                                           trafficOrders,
                                                           toggleModal,
                                                           modalActions,
                                                           hasNextPage = false,
                                                           onLoadMore,
                                                       }) => {

    const handleLoadMore = () => {
        if (hasNextPage && !isFetchingNext) onLoadMore?.()
    }

    const actions: ModalActionsMapType = useMemo(() => {
        if (!modalActions) return {}

        const addAction = (key: OrderActionKey) =>
            modalActions.includes(key) ? (id: string, date?: string) => toggleModal(ACTION_TO_MODAL[key], id, date) : undefined;

        const actionsArray =
            Object.keys(ACTION_TO_MODAL).map(k => ({[k as OrderActionKey]: addAction(k as OrderActionKey)}))

        return Object.assign({}, ...Object.values(actionsArray));
    }, [modalActions, toggleModal]);

    const sentinelRef = useInfiniteScroll(handleLoadMore, {
        enabled: hasNextPage && !isFetchingNext && !isLoading,
    });

    if (isLoading) {
        return (
            <Card>
                <CardBody>
                    <Loader/>
                    {!trafficOrders?.length && <NoSearchResult text="Loading..."/>}
                </CardBody>
            </Card>
        );
    }

    if (isError || !isSuccess) {
        return (
            <Card>
                <CardBody>
                    <NoSearchResult text="Error loading data"/>
                </CardBody>
            </Card>
        );
    }

    return (
        <Card>
            <CardBody>
                <Row>
                    {!trafficOrders?.length && <NoSearchResult text="No traffic orders available"/>}

                    {trafficOrders?.map((order) => (
                        <CardOrder
                            key={order.id}
                            data={order}
                            activeTabId={activeTabId}
                            toggleAssignOrder={actions.toggleAssignOrder}
                            toggleAssignIntegra={actions.toggleAssignIntegra}
                            toggleInfoOrder={actions.toggleInfoOrder}
                            toggleUpdate={actions.toggleUpdate}
                            toggleChangeStatus={
                                actions.toggleChangeStatus &&
                                ((id: string) => actions.toggleChangeStatus!(id, order.current_capas?.date))
                            }
                        />
                    ))}
                </Row>

                {hasNextPage && (
                    <>
                        <div ref={sentinelRef}/>
                        <div className="text-center my-3">
                            <Button className="w-100" style={{maxWidth: "120px"}} color="primary"
                                    onClick={handleLoadMore} disabled={isFetchingNext}>
                                {isFetchingNext ? "Loading..." : "Load More"}
                            </Button>
                        </div>
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default React.memo(TabPaneContent);